import * as React from 'react';
import { useIntl } from 'react-intl';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { CmdInput, CmdModal, CmdModalFooterButton } from '@commander-services/gui-components';
import { AxiosResponse } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { getRequest } from '../../Services/HttpService';
import { TInputType } from '../Forms/ReactHookFormFields/LoginInput';
import { transformErrors, validateFields } from '../../Services/HookFormService';

interface IVehicleGroupFormProps {
  closeCallback: () => void;
  responseCallback: (values: string) => void;
}

interface IValidationRule {
  param: 'minLength' | 'maxLength' | 'required';
  value: number | boolean;
  message: string;
}

interface IInput {
  indexId: string;
  title: string;
  type: string;
  readonly: boolean;
  disabled: boolean;
  value: string;
  placeholder: string;
  rules: IValidationRule[];
}

const resolver: Resolver = async (formData, context) => {
  const { validation } = context;
  const validateErrors = validateFields(formData, validation);
  const formErrors = transformErrors(validateErrors);

  return {
    values: formErrors && Object.keys(formErrors).length ? {} : formData,
    errors: formErrors,
  };
};

function VehicleGroupForm(props: IVehicleGroupFormProps) {
  const { formatMessage: f } = useIntl();
  const [validation, setValidation] = React.useState<Record<string, IValidationRule[]>>({});

  const [data, setData] = React.useState<IInput[]>([
    {
      indexId: 'assignToVehicleGroup',
      title: f({ id: 'carsGroups.groupTitle' }),
      type: 'text',
      readonly: false,
      disabled: false,
      value: '',
      placeholder: '',
      rules: [],
    },
  ]);

  const fields: IInput[] = Object.assign(data);

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: {
      assignToVehicleGroup: '',
    },
    resolver,
    context: { validation },
  });

  const getValidation = async () => {
    const response: AxiosResponse | false = await getRequest(
      '/v1/alarm/validation/assignToVehicleGroup',
      {
        returnAxiosResponse: true,
      }
    );

    if ((response as AxiosResponse).data && (response as AxiosResponse).status === 200) {
      const inputs = (response as AxiosResponse).data;
      const newValidation: Record<string, IValidationRule[]> = {};
      Object.values(fields).forEach((item) => {
        Object.keys(inputs).forEach((input) => {
          if (item.indexId === input) {
            if (inputs[item.indexId]) {
              item.rules = inputs[item.indexId];
              newValidation[item.indexId] = inputs[item.indexId];
            }
          }
        });
      });
      setData({
        ...data,
        ...fields,
      });
      setValidation(newValidation);
    }
  };

  const { isLoading: isLoadingValidation } = useQuery({
    queryKey: ['vehicleGroupForm-alarm'],
    queryFn: () => getValidation(),
    staleTime: 9000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const onSubmit = async (values) => {
    props.responseCallback(values.assignToVehicleGroup);
    props.closeCallback();
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'vehiclegroup-form-submit',
      type: 'button',
      className: 'e-button e-button--gray',
      title: f({ id: 'alarms.modal.groups.submit' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];
  const isLoading = isLoadingValidation;

  return (
    <CmdModal
      id="vehiclegroup-form-modal"
      title={f({ id: 'alarms.modal.groups.title' })}
      footerButtons={buttons}
      loader={isLoading}
    >
      <Controller
        name="assignToVehicleGroup"
        control={control}
        render={({ field }) => (
          <CmdInput
            {...field}
            label={fields[0].title}
            id={fields[0].indexId}
            type={fields[0].type as TInputType}
            placeholder={fields[0].placeholder}
            required={fields[0].rules.some(
              (rule) => rule.param === 'required' && rule.value === true
            )}
            error={errors?.assignToVehicleGroup?.message as string}
            touched={true}
            onChangeCallback={(value) => {
              setValue('assignToVehicleGroup', value);
              trigger('assignToVehicleGroup');
            }}
          />
        )}
      />
    </CmdModal>
  );
}

export default VehicleGroupForm;
